import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiUser, FiUsers } from 'react-icons/fi';
import dayjs from 'dayjs';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../../helpers/i18n/toastI18n';
import { ArrowLeft } from 'lucide-react';

// Interfaces
import { Patient } from '../../../interfaces/patient';
import { DicomPatientProfile } from '../NewCaseReverseUpload';

// API Helpers
import {
  usePaginatedPatients,
  useCreatePatientWithPregnancy,
} from '../../../../helpers/api/patients/patients';
import {
  useCreatePregnancy,
  PregnancyData,
} from '../../../../helpers/api/pregnancy/pregnancy';

// Components
import {
  AddPatientAndPregnancyForm,
  CombinedFormData,
} from '../../features/AddPatientAndPregnancyForm';
import { Pagination } from '../../common/Pagination';
import { Button } from '../../common/Button';
import { PatientSelectionList } from '../../features/PatientSelectionListNew';
import { Modal } from '../../common/Modal';
import PregnancyModal from '../../WorklistSearch/PregnancyModal';
import { computeGAFromLmp } from '../utils';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  wizardState: any;
  setWizardState: React.Dispatch<React.SetStateAction<any>>;
  isPatientLocked?: boolean;
  onDirectSaveFromPregnancy?: (worklistItem: any, pregnancyData: any) => void;
  onViewChange?: (view: 'search' | 'add') => void;
  onFormValidityChange?: (isValid: boolean) => void;
  onSaveAndConfirmViews?: () => void;
}

type Trimester = 'T1' | 'T2' | 'T3';
type PregnancyStatus =
  | 'Active'
  | 'Delivered'
  | 'Miscarried'
  | 'Terminated'
  | 'Unknown';

interface PregnancyFormState {
  lmp: string;
  edd: string;
  gaWeeks: string;
  gaDays: string;
  crl: string;
  bpd: string;
  hc: string;
  ac: string;
  fl: string;
  status?: PregnancyStatus;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string;
}

const Step2_PatientAndPregnancy = ({
  wizardState,
  setWizardState,
  isPatientLocked = false,
  onDirectSaveFromPregnancy,
  onViewChange,
  onFormValidityChange,
  onSaveAndConfirmViews,
}: Props) => {
  const { t } = useTranslation();

  // --- STATE ---
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [activeDicomProfile, setActiveDicomProfile] =
    useState<DicomPatientProfile | null>(null);

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(
    wizardState.patient
  );
  const [view, setView] = useState<'search' | 'add'>('search');
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const addPatientFormRef = useRef<HTMLFormElement>(null);

  // Pagination & Filtering
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 4 });
  const [filters, setFilters] = useState({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [initialAutoSelectDone, setInitialAutoSelectDone] = useState(false);
  const [initialViewCheckComplete, setInitialViewCheckComplete] =
    useState(false);
  const [displayedPatients, setDisplayedPatients] = useState<Patient[]>([]);

  const [pregnancyModalOpen, setPregnancyModalOpen] = useState(false);
  /** Prevents auto-open from firing again after close or when list refresh replaces `selectedPatient` by reference. */
  const autoCreatePregnancyModalShownForId = useRef<string | null>(null);
  const [selectedPatientForPregnancy, setSelectedPatientForPregnancy] =
    useState<Patient | null>(null);
  const [pregnancyModalMode, setPregnancyModalMode] = useState<
    'create' | 'edit-local' | 'edit-backend'
  >('create');
  const [pregnancyInitialData, setPregnancyInitialData] = useState<
    Partial<PregnancyFormState & { trimester: Trimester }> | undefined
  >(undefined);
  const [editingPregnancyId, setEditingPregnancyId] = useState<
    number | undefined
  >(undefined);

  const [tempSelectedProfile, setTempSelectedProfile] =
    useState<DicomPatientProfile | null>(null);

  // Combined form data state
  const [combinedFormData, setCombinedFormData] =
    useState<CombinedFormData | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [pendingSaveAndConfirm, setPendingSaveAndConfirm] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (onViewChange) {
      onViewChange(view);
    }
  }, [view, onViewChange]);

  useEffect(() => {
    if (onFormValidityChange) {
      onFormValidityChange(isFormValid);
    }
  }, [isFormValid, onFormValidityChange]);

  // --- 1. AUTO-DETECT PATIENT FROM DICOM ---
  useEffect(() => {
    if (isPatientLocked || wizardState.patient || activeDicomProfile) return;

    const extracted = wizardState.extractedPatients || [];
    if (extracted.length === 1) {
      setActiveDicomProfile(extracted[0]);
    } else if (extracted.length > 1) {
      setIsSelectionModalOpen(true);
    }
  }, [
    wizardState.extractedPatients,
    isPatientLocked,
    activeDicomProfile,
    wizardState.patient,
  ]);

  // --- 2. PATIENT SEARCH QUERIES ---
  const buildApiFilters = (currentFilters: object) => {
    const updatedFilters: { [key: string]: string } = {};
    Object.keys(currentFilters).forEach((key) => {
      if (currentFilters[key]) {
        updatedFilters[`${key}__ilike`] = `%${currentFilters[key]}%`;
      }
    });
    return { condition__or: updatedFilters };
  };

  const { data: patientMatchData, isLoading: isLoadingMatch } =
    usePaginatedPatients(
      1,
      1,
      {
        filters: JSON.stringify(
          buildApiFilters({ patient_name: activeDicomProfile?.patientName })
        ),
      },
      {
        enabled: !!activeDicomProfile?.patientName && !initialViewCheckComplete,
        queryKey: ['patientMatchByName', activeDicomProfile?.patientName],
      }
    );

  const queryOptions: any = {};
  if (Object.keys(filters).length > 0) {
    queryOptions.filters = JSON.stringify(buildApiFilters(filters));
  }

  const {
    data: patientListData,
    isLoading: isLoadingList,
    isError,
    refetch,
  } = usePaginatedPatients(
    pagination.pageIndex + 1,
    pagination.pageSize,
    queryOptions,
    { queryKey: ['patientList', pagination, queryOptions] }
  );

  // --- 3. PREGNANCY DATA QUERY ---
  const { mutate: createOrUpdatePregnancy, isPending: isSavingPregnancy } =
    useCreatePregnancy(
      (response) => {
        messageI18n.success('newCase.pregnancyRecordSaved');
        const pregnancyData = response?.data?.data || response?.data;

        const updatedPatients = displayedPatients.map((p) => {
          if (p.id === selectedPatientForPregnancy?.id) {
            return {
              ...p,
              pregnancy: pregnancyData,
            } as Patient;
          }
          return p;
        });

        setDisplayedPatients(updatedPatients);

        if (selectedPatient?.id === selectedPatientForPregnancy?.id) {
          const updatedSelectedPatient = {
            ...selectedPatient,
            pregnancy: pregnancyData,
          } as Patient;
          setSelectedPatient(updatedSelectedPatient);
          setWizardState((prev) => ({
            ...prev,
            patient: updatedSelectedPatient,
            pregnancy: pregnancyData,
          }));
        }
      },
      (error) => {
        messageI18n.error('newCase.pregnancySaveFailed');
        console.error(error);
      }
    );

  // --- 4. CREATE PATIENT WITH PREGNANCY MUTATION ---
  const { mutate: createPatientWithPregnancy } = useCreatePatientWithPregnancy(
    (response) => {
      const responseData = response?.data?.data || response?.data;
      const newPatient = responseData?.patient || responseData;
      const pregnancy = responseData?.pregnancy;
      const pregnancyError = responseData?.pregnancy_error;

      queryClient.refetchQueries({ queryKey: ['paginatedPatient'] });
      queryClient.refetchQueries({ queryKey: ['analytics'] });

      let patientWithPregnancy = newPatient;

      if (pregnancyError) {
        messageI18n.warning('newCase.patientCreatedPregnancyFailed', {
          message: pregnancyError.message,
        });
        setSelectedPatient(newPatient);
        setWizardState((prev) => ({
          ...prev,
          patient: newPatient,
          pregnancy: null,
        }));
      } else if (pregnancy) {
        patientWithPregnancy = {
          ...newPatient,
          pregnancy: pregnancy,
        };
        setSelectedPatient(patientWithPregnancy);
        setWizardState((prev) => ({
          ...prev,
          patient: patientWithPregnancy,
          pregnancy: pregnancy,
        }));
        setDisplayedPatients((prev) => {
          const exists = prev.some((p) => p.id === patientWithPregnancy.id);
          if (exists) {
            return prev.map((p) =>
              p.id === patientWithPregnancy.id ? patientWithPregnancy : p
            );
          }
          return [patientWithPregnancy, ...prev];
        });
      } else {
        setSelectedPatient(newPatient);
        setWizardState((prev) => ({
          ...prev,
          patient: newPatient,
          pregnancy: null,
        }));
      }

      setIsFormSubmitting(false);

      // If "Save and Confirm Views" was clicked, trigger the direct save callback
      if (pendingSaveAndConfirm) {
        setPendingSaveAndConfirm(false);
        if (pregnancy) {
          messageI18n.success('newCase.patientAndPregnancyCreatedSaving');
        } else {
          messageI18n.success('newCase.patientCreatedSaving');
        }

        // Prepare pregnancy data in the format expected by the parent
        const pregnancyDataForSave = pregnancy
          ? {
              id: pregnancy.id,
              patient_id: pregnancy.patient_id,
              lmp: pregnancy.last_menstrual_period || null,
              edd: pregnancy.estimated_due_date || null,
              crl: pregnancy.preg_crl || null,
              bpd: pregnancy.preg_bpd || null,
              hc: pregnancy.preg_hc || null,
              ac: pregnancy.preg_ac || null,
              fl: pregnancy.preg_fl || null,
              existsInBackend: true,
              isFromLocalForm: true,
            }
          : null;

        if (onDirectSaveFromPregnancy) {
          onDirectSaveFromPregnancy(patientWithPregnancy, pregnancyDataForSave);
        } else if (onSaveAndConfirmViews) {
          setTimeout(() => {
            onSaveAndConfirmViews();
          }, 100);
        }
      } else {
        setView('search');
        if (pregnancy) {
          messageI18n.success('newCase.patientAndPregnancyCreated');
        } else {
          messageI18n.success('newCase.patientCreated');
        }
      }
    },
    (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        tToast('newCase.patientPregnancyCreateFailed');
      message.error(errorMessage);
      setIsFormSubmitting(false);
      setPendingSaveAndConfirm(false);
      console.error('Patient and pregnancy creation error:', error);
    }
  );

  useEffect(() => {
    if (initialViewCheckComplete || isLoadingMatch || !activeDicomProfile) {
      if (
        !activeDicomProfile &&
        !wizardState.extractedPatients?.length &&
        !initialViewCheckComplete
      ) {
        setInitialViewCheckComplete(true);
      }
      return;
    }
    const matchFound = patientMatchData?.items?.[0];
    if (!matchFound) {
      setView('add');
    }
    setInitialViewCheckComplete(true);
  }, [
    patientMatchData,
    isLoadingMatch,
    initialViewCheckComplete,
    activeDicomProfile,
    wizardState.extractedPatients,
  ]);

  useEffect(() => {
    const active = wizardState.pregnancy;

    if (active?.pregnancy_status === 'Active') {
      if (!wizardState.pregnancy) {
        setWizardState((p) => ({ ...p, pregnancy: active }));
      }
    } else if (selectedPatient) {
      setWizardState((p) => ({ ...p, pregnancy: null }));
    }
  }, [wizardState.pregnancy, selectedPatient, setWizardState]);

  useEffect(() => {
    const isInitialLoad =
      pagination.pageIndex === 0 &&
      Object.keys(filters).length === 0 &&
      !hasUserInteracted;
    const firstPageList = patientListData?.items;
    const patientToFeature =
      wizardState.patient || patientMatchData?.items?.[0];

    const mergePatientData = (apiPatients: Patient[]) => {
      return apiPatients.map((apiPatient) => {
        const existingPatient = displayedPatients.find(
          (p) => p.id === apiPatient.id
        );
        if (existingPatient && (existingPatient as any).pregnancy) {
          const existingPregnancy = (existingPatient as any).pregnancy;
          const apiPregnancy = (apiPatient as any).pregnancy;

          if (!apiPregnancy && existingPregnancy) {
            return {
              ...apiPatient,
              pregnancy: existingPregnancy,
            } as Patient;
          }
        }
        return apiPatient;
      });
    };

    if (!isInitialLoad) {
      setDisplayedPatients(mergePatientData(firstPageList || []));
      return;
    }

    if (firstPageList) {
      const mergedList = mergePatientData(firstPageList);
      if (patientToFeature) {
        const isAlreadyPresent = mergedList.some(
          (p) => p.id === patientToFeature.id
        );
        if (isAlreadyPresent) {
          setDisplayedPatients(mergedList);
        } else {
          setDisplayedPatients([
            patientToFeature,
            ...mergedList.slice(0, pagination.pageSize - 1),
          ]);
        }
      } else {
        setDisplayedPatients(mergedList);
      }
    } else if (patientToFeature) {
      setDisplayedPatients([patientToFeature]);
    }
  }, [
    patientListData,
    patientMatchData,
    pagination,
    filters,
    hasUserInteracted,
    wizardState.patient,
  ]);

  // Auto-Select Logic
  useEffect(() => {
    const patientToSelect = wizardState.patient || patientMatchData?.items?.[0];
    if (patientToSelect && !selectedPatient && !initialAutoSelectDone) {
      setSelectedPatient(patientToSelect);
      if (!wizardState.patient) {
        setWizardState((p) => ({ ...p, patient: patientToSelect }));
      }
      setInitialAutoSelectDone(true);
    }
  }, [
    patientMatchData,
    wizardState.patient,
    selectedPatient,
    initialAutoSelectDone,
    setWizardState,
  ]);

  useEffect(() => {
    if (!selectedPatient) {
      autoCreatePregnancyModalShownForId.current = null;
      return;
    }

    const pregnancy = (selectedPatient as any).pregnancy;
    const hasActivePregnancy = pregnancy?.pregnancy_status === 'Active';
    const id = String(selectedPatient.id);

    if (hasActivePregnancy) {
      autoCreatePregnancyModalShownForId.current = null;
      return;
    }

    if (!hasUserInteracted || pregnancyModalOpen) return;

    if (autoCreatePregnancyModalShownForId.current === id) return;

    autoCreatePregnancyModalShownForId.current = id;
    setSelectedPatientForPregnancy(selectedPatient);
    setPregnancyModalMode('create');
    setPregnancyInitialData(undefined);
    setEditingPregnancyId(undefined);
    setPregnancyModalOpen(true);
  }, [selectedPatient, hasUserInteracted, pregnancyModalOpen]);

  // --- 6. HANDLERS ---

  const handlePatientSelect = (patient: Patient) => {
    if (isPatientLocked) return;
    setHasUserInteracted(true);
    if (selectedPatient?.id === patient.id) {
      setSelectedPatient(null);
      setWizardState((p) => ({ ...p, patient: null, pregnancy: null }));
    } else {
      const patientWithPregnancy =
        displayedPatients.find((p) => p.id === patient.id) || patient;
      const pregnancy = (patientWithPregnancy as any).pregnancy || null;

      setSelectedPatient(patientWithPregnancy);
      setWizardState((p) => ({
        ...p,
        patient: patientWithPregnancy,
        pregnancy: pregnancy,
      }));
      setView('search');
    }
  };

  const handleProfileSelectionConfirm = (profile: DicomPatientProfile) => {
    setActiveDicomProfile(profile);
    setIsSelectionModalOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setHasUserInteracted(true);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    if (value === '') setFilters({});
    else
      setFilters({
        patient_name: value,
        patient_last_name: value,
        email: value,
      });
  };

  const handlePageChange = (pageNumber: number) => {
    setHasUserInteracted(true);
    setPagination((p) => ({ ...p, pageIndex: pageNumber - 1 }));
  };

  const handleAddPregnancy = (patient: Patient) => {
    setSelectedPatientForPregnancy(patient);
    setPregnancyModalMode('create');
    setPregnancyInitialData(undefined);
    setEditingPregnancyId(undefined);
    autoCreatePregnancyModalShownForId.current = String(patient.id);
    setPregnancyModalOpen(true);
  };

  const handleEditPregnancy = (patient: Patient) => {
    setSelectedPatientForPregnancy(patient);
    const pregnancy = (patient as any).pregnancy;

    if (pregnancy) {
      const lmpStr = pregnancy.last_menstrual_period
        ? dayjs(pregnancy.last_menstrual_period).format('YYYY-MM-DD')
        : '';

      let gaWeeks = 0;
      let gaDays = 0;
      if (lmpStr) {
        const us = pregnancy.ultrasound_date
          ? dayjs(pregnancy.ultrasound_date).format('YYYY-MM-DD')
          : undefined;
        const ga = computeGAFromLmp(lmpStr, us);
        if (ga) {
          gaWeeks = ga.weeks;
          gaDays = ga.days;
        }
      }

      const initialData: Partial<
        PregnancyFormState & { trimester: Trimester }
      > = {
        lmp: lmpStr,
        edd: pregnancy.estimated_due_date
          ? dayjs(pregnancy.estimated_due_date).format('YYYY-MM-DD')
          : '',
        gaWeeks: gaWeeks > 0 ? gaWeeks.toString() : '',
        gaDays: gaDays > 0 ? gaDays.toString() : '',
        crl: pregnancy.preg_crl?.toString() || '',
        bpd: pregnancy.preg_bpd?.toString() || '',
        hc: pregnancy.preg_hc?.toString() || '',
        ac: pregnancy.preg_ac?.toString() || '',
        fl: pregnancy.preg_fl?.toString() || '',
        grossesse_classee_a_risque:
          pregnancy.grossesse_classee_a_risque ?? null,
        risque_anemie: pregnancy.risque_anemie === true,
        risque_hta: pregnancy.risque_hta === true,
        risque_diabete: pregnancy.risque_diabete === true,
        risque_cardiopathie: pregnancy.risque_cardiopathie === true,
        risque_metrorragie: pregnancy.risque_metrorragie === true,
        risque_infection: pregnancy.risque_infection === true,
        risque_preeclampsie: pregnancy.risque_preeclampsie === true,
        risque_eclampsie: pregnancy.risque_eclampsie === true,
        risque_autre_details: pregnancy.risque_autre_details || '',
        status: pregnancy.pregnancy_status as any,
        trimester: gaWeeks >= 28 ? 'T3' : gaWeeks >= 14 ? 'T2' : 'T1',
      };

      setPregnancyModalMode('edit-backend');
      setPregnancyInitialData(initialData);
      setEditingPregnancyId(pregnancy.id);
    }
    setPregnancyModalOpen(true);
  };

  const handleSaveLocalPregnancy = (
    pregnancyData: PregnancyFormState & { trimester: Trimester }
  ) => {
    if (!selectedPatientForPregnancy) return;

    // Convert local pregnancy data to API payload
    const payload: PregnancyData = {
      patient_id: selectedPatientForPregnancy.id,
      dating_method: 'LMP',
      last_menstrual_period: pregnancyData.lmp || undefined,
      estimated_due_date: pregnancyData.edd || undefined,
      preg_bpd: pregnancyData.bpd ? parseFloat(pregnancyData.bpd) : undefined,
      preg_hc: pregnancyData.hc ? parseFloat(pregnancyData.hc) : undefined,
      preg_ac: pregnancyData.ac ? parseFloat(pregnancyData.ac) : undefined,
      preg_fl: pregnancyData.fl ? parseFloat(pregnancyData.fl) : undefined,
      preg_crl: pregnancyData.crl ? parseFloat(pregnancyData.crl) : undefined,
      pregnancy_status: pregnancyData.status || 'Active',
      grossesse_classee_a_risque: pregnancyData.grossesse_classee_a_risque,
      risque_anemie: pregnancyData.risque_anemie === true,
      risque_hta: pregnancyData.risque_hta === true,
      risque_diabete: pregnancyData.risque_diabete === true,
      risque_cardiopathie: pregnancyData.risque_cardiopathie === true,
      risque_metrorragie: pregnancyData.risque_metrorragie === true,
      risque_infection: pregnancyData.risque_infection === true,
      risque_preeclampsie: pregnancyData.risque_preeclampsie === true,
      risque_eclampsie: pregnancyData.risque_eclampsie === true,
      risque_autre_details: pregnancyData.risque_autre_details?.trim() || null,
    };

    if (
      pregnancyData.bpd ||
      pregnancyData.hc ||
      pregnancyData.ac ||
      pregnancyData.fl ||
      pregnancyData.crl
    ) {
      payload.dating_method = 'ULTRASOUND';
    } else if (pregnancyData.edd && !pregnancyData.lmp) {
      payload.dating_method = 'EDD';
    }

    createOrUpdatePregnancy(payload);
  };

  const handleBackendPregnancyUpdate = (updatedPregnancy: any) => {
    const updatedPatients = displayedPatients.map((patient) => {
      const patientPregnancy = (patient as any).pregnancy;
      if (patientPregnancy && patientPregnancy.id === updatedPregnancy.id) {
        return {
          ...patient,
          pregnancy: updatedPregnancy,
        } as Patient;
      }
      return patient;
    });

    setDisplayedPatients(updatedPatients);

    if (
      selectedPatient &&
      (selectedPatient as any).pregnancy?.id === updatedPregnancy.id
    ) {
      const updatedPatient = {
        ...selectedPatient,
        pregnancy: updatedPregnancy,
      } as Patient;
      setSelectedPatient(updatedPatient);
      setWizardState((p) => ({
        ...p,
        patient: updatedPatient,
        pregnancy: updatedPregnancy,
      }));
    }
  };

  const handleSaveAndConfirmFromPregnancy = (pregnancyData: any) => {
    if (!selectedPatientForPregnancy) return;

    const worklistItem = patientToWorklistItem(selectedPatientForPregnancy);

    setPregnancyModalOpen(false);

    if (onDirectSaveFromPregnancy && worklistItem) {
      onDirectSaveFromPregnancy(worklistItem, pregnancyData);
    } else {
      handleSaveLocalPregnancy(pregnancyData);
    }
  };

  const patientToWorklistItem = (patient: Patient) => {
    if (!patient) return null;

    const patientAny = patient as any;
    return {
      mwl: {
        PatientName: `${patient.patient_last_name}^${patient.patient_name}`,
        PatientID: patientAny.patient_id || patient.id,
        PatientBirthDate: patient.birth_date
          ? dayjs(patient.birth_date).format('YYYYMMDD')
          : '',
        PatientSex: 'F',
      },
      resolution: {
        patient_exists: true,
        patient_uuid: patient.id,
        patient_id_internal: patientAny.patient_id || patient.id,
        active_pregnancy_exists:
          patientAny.pregnancy?.pregnancy_status === 'Active',
        pregnancy: patientAny.pregnancy || null,
        study_exists: false,
        study_instance_uid: null,
      },
    } as any;
  };

  const buildPayloads = () => {
    if (!combinedFormData) return null;

    const patientPayload: any = Object.fromEntries(
      Object.entries(combinedFormData.patient).filter(
        ([_, v]) => v !== null && v !== ''
      )
    );

    const p = combinedFormData.pregnancy;
    const pregnancyPayload: any = {
      dating_method: 'LMP',
      last_menstrual_period: p.lmp || undefined,
      estimated_due_date: p.edd || undefined,
      preg_bpd: p.bpd ? parseFloat(p.bpd) : undefined,
      preg_hc: p.hc ? parseFloat(p.hc) : undefined,
      preg_ac: p.ac ? parseFloat(p.ac) : undefined,
      preg_fl: p.fl ? parseFloat(p.fl) : undefined,
      preg_crl: p.crl ? parseFloat(p.crl) : undefined,
    };

    if (p.bpd || p.hc || p.ac || p.fl || p.crl) {
      pregnancyPayload.dating_method = 'ULTRASOUND';
    } else if (p.edd && !p.lmp) {
      pregnancyPayload.dating_method = 'EDD';
    }

    return { patientPayload, pregnancyPayload };
  };

  const handleCombinedFormSubmit = () => {
    if (!combinedFormData || !isFormValid) {
      messageI18n.warning('newCase.fillRequiredFields');
      return;
    }

    const payloads = buildPayloads();
    if (!payloads) return;

    setIsFormSubmitting(true);
    setPendingSaveAndConfirm(false);

    createPatientWithPregnancy({
      patient: payloads.patientPayload,
      pregnancy: payloads.pregnancyPayload,
    });
  };

  const handleSaveAndConfirmViews = () => {
    if (!combinedFormData || !isFormValid) {
      messageI18n.warning('newCase.fillRequiredFields');
      return;
    }

    const payloads = buildPayloads();
    if (!payloads) return;

    setIsFormSubmitting(true);
    setPendingSaveAndConfirm(true);

    createPatientWithPregnancy({
      patient: payloads.patientPayload,
      pregnancy: payloads.pregnancyPayload,
    });
  };

  useEffect(() => {
    if (view === 'search') refetch();
  }, [view, refetch]);

  // --- 7. RENDER SUB-COMPONENTS ---

  const renderPatientSelectionModal = () => {
    return (
      <Modal isOpen={isSelectionModalOpen} onClose={() => {}} size="lg">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 bg-amber-100 dark:bg-amber-900/50 p-3 rounded-full">
              <FiUsers className="h-6 w-6 text-amber-600 dark:text-amber-300" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white">
                Multiple Patients Detected
              </h3>
              <p className="text-sm text-slate-500 dark:text-gray-400">
                The uploaded files contain information for multiple patients.
                Please select the correct patient for this case.
              </p>
            </div>
          </div>

          <div className="my-6 space-y-2 max-h-60 overflow-y-auto">
            {(wizardState.extractedPatients || []).map((profile, index) => (
              <div
                key={index}
                onClick={() => setTempSelectedProfile(profile)}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                  tempSelectedProfile?.patientName === profile.patientName
                    ? 'bg-blue-50 dark:bg-blue-900/50 border-blue-500'
                    : 'bg-card-color-100 dark:bg-gray-800 border-slate-300 dark:border-gray-700 hover:border-slate-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex-shrink-0 mr-4">
                  <FiUser className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-medium text-title-page-color-100 dark:text-gray-200">
                    {profile.patientName || 'Unknown Name'}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                    ID: {profile.patientID || 'N/A'} &bull; DoB:{' '}
                    {profile.patientBirthDate
                      ? dayjs(profile.patientBirthDate, 'YYYYMMDD').format(
                          'DD MMM YYYY'
                        )
                      : 'N/A'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-gray-700">
            <Button
              onClick={() =>
                handleProfileSelectionConfirm(tempSelectedProfile!)
              }
              disabled={!tempSelectedProfile}
            >
              Confirm and Continue
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  // --- MAIN RENDER ---
  return (
    <div className="flex flex-col h-full bg-card-color-200 dark:bg-gray-800 overflow-hidden">
      <PregnancyModal
        isOpen={pregnancyModalOpen}
        onClose={() => setPregnancyModalOpen(false)}
        worklistItem={patientToWorklistItem(selectedPatientForPregnancy!)}
        onSaveLocal={handleSaveLocalPregnancy}
        onUpdateSuccess={handleBackendPregnancyUpdate}
        onSaveAndConfirm={
          onDirectSaveFromPregnancy
            ? handleSaveAndConfirmFromPregnancy
            : undefined
        }
        initialData={pregnancyInitialData}
        mode={pregnancyModalMode}
        pregnancyId={editingPregnancyId}
        ultrasoundDate={(() => {
          if (!selectedPatientForPregnancy) return undefined;
          const raw = (selectedPatientForPregnancy as any).pregnancy
            ?.ultrasound_date;
          if (!raw) return undefined;
          const d = dayjs(raw);
          return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
        })()}
      />
      {renderPatientSelectionModal()}

      {!isSelectionModalOpen && (
        <>
          <div className="px-4 py-3 flex justify-between items-center border-b border-slate-200 dark:border-gray-700 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Patient & Pregnancy
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {isPatientLocked
                  ? `Confirm pregnancy details for ${wizardState.patient?.patient_name}.`
                  : view === 'add'
                    ? 'Fill in the details for the new patient.'
                    : 'Select an existing patient or add a new one.'}
              </p>
            </div>
            {!isPatientLocked &&
              (view === 'search' ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setView('add')}
                >
                  <FiPlus className="mr-2 h-4 w-4" /> Add New Patient
                </Button>
              ) : (
                <div className="flex justify-between items-center space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() => setView('search')}
                    className="p-2 h-auto"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1 text-title-page-color-300 dark:text-gray-400" />
                    {t('back')}
                  </Button>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleCombinedFormSubmit}
                      disabled={isFormSubmitting || !isFormValid}
                    >
                      {isFormSubmitting && !pendingSaveAndConfirm
                        ? 'Saving...'
                        : 'Save and Select Patient'}
                    </Button>
                    {onSaveAndConfirmViews && (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={handleSaveAndConfirmViews}
                        disabled={isFormSubmitting || !isFormValid}
                      >
                        {isFormSubmitting && pendingSaveAndConfirm
                          ? 'Saving...'
                          : 'Confirm & Save Views'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
          </div>

          <div className="flex-1 px-4 py-3 overflow-y-auto min-h-0">
            {view === 'search' ? (
              <div className="space-y-3">
                <PatientSelectionList
                  patients={displayedPatients}
                  isLoading={
                    isLoadingList ||
                    (isLoadingMatch && !initialViewCheckComplete)
                  }
                  isError={isError}
                  onPatientSelect={handlePatientSelect}
                  onSearchChange={handleSearchChange}
                  selectedPatient={selectedPatient}
                  isSearchDisabled={isPatientLocked}
                  onAddPregnancy={handleAddPregnancy}
                  onEditPregnancy={handleEditPregnancy}
                />
                {!isPatientLocked &&
                  patientListData &&
                  patientListData.total_count > 0 && (
                    <div className="mt-3">
                      <Pagination
                        currentPage={pagination.pageIndex + 1}
                        totalPages={patientListData?.total_pages || 1}
                        totalItems={patientListData?.total_count || 0}
                        itemsPerPage={pagination.pageSize}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
              </div>
            ) : (
              <AddPatientAndPregnancyForm
                ref={addPatientFormRef}
                onFormDataChange={setCombinedFormData}
                onValidationChange={setIsFormValid}
                initialPatientData={
                  activeDicomProfile
                    ? {
                        patient_name: activeDicomProfile.firstName || '',
                        patient_last_name: activeDicomProfile.lastName || '',
                        birth_date:
                          activeDicomProfile.patientBirthDate &&
                          activeDicomProfile.patientBirthDate.length === 8
                            ? dayjs(
                                activeDicomProfile.patientBirthDate,
                                'YYYYMMDD'
                              ).format('YYYY-MM-DD')
                            : '',
                        patient_middle_name: '',
                        email: '',
                        // phone_number: '',
                        address: '',
                        policy_number: '',
                        insurance_provider: '',
                        emergency_details: '',
                      }
                    : undefined
                }
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Step2_PatientAndPregnancy;
